import { Link, Outlet, useLocation } from '@tanstack/react-router';
import { BookOpen, Cpu, FileText, LayoutDashboard, ListChecks } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { cn } from '~/utils/cn';

import { ContentHealthBanner } from './content-health-banner';
import { preloadQcPages } from './qc/qc-preload';

/** One tab (or Settings menu entry) in the Content section's tab bar. */
interface ContentNavItem {
  /** Leaf URL the tab navigates to — the most specific path, so the router
   *  doesn't chase an index-route redirect on every click. */
  to: string;
  label: string;
  icon: LucideIcon;
  /** Warms the tab's page chunks on hover/focus, so the click doesn't wait on a download. */
  preload?: () => void;
}

/**
 * Everyday work as tabs: Overview (the section's landing — `/content` redirects there), then Runs
 * (QC checks and catalog runs in one list). Old URLs (`/content/jobs`, `/content/qc`,
 * `/content/attribution`, `/content/review`) redirect to Runs. Gated by the `content_generation`
 * screen at the route.
 */
const workTabs: ContentNavItem[] = [
  { to: '/content/overview', label: 'Overview', icon: LayoutDashboard },
  { to: '/content/runs', label: 'Runs', icon: ListChecks, preload: preloadQcPages },
];

/** Admin-level setup, shown after a divider so everyday work comes first. */
const settingsItems: ContentNavItem[] = [
  { to: '/content/settings/models', label: 'AI models', icon: Cpu },
  { to: '/content/settings/instructions', label: 'AI instructions', icon: FileText },
  { to: '/content/settings/vocabulary', label: 'Vocabulary sync', icon: BookOpen },
];

/** Underline-tab look shared with the Integrations section's tab bar. */
function tabClass(isActive: boolean): string {
  return cn(
    '-mb-px flex shrink-0 cursor-pointer items-center gap-2 border-b-2 px-1 pb-2.5 text-sm font-medium whitespace-nowrap transition-colors',
    'focus-visible:ring-ring rounded-none focus-visible:ring-2 focus-visible:outline-none',
    isActive
      ? 'border-primary text-primary'
      : 'text-muted-foreground hover:text-foreground border-transparent',
  );
}

/** Bounded prefix match — `path` OR `path + '/'` — so `/content/runs-archive` can't light up Runs. */
function isUnder(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(prefix + '/');
}

/**
 * Content section shell, mounted by `/content/route.tsx` — the same chrome as Integrations:
 * a compact title on the page gradient, then one card whose header strip is the tab bar
 * and whose body scrolls the active page (each wraps itself in `ContentPage`).
 */
export function ContentLayout() {
  const { pathname } = useLocation();

  return (
    <div className="bg-gradient-page flex h-full flex-col gap-4 p-6 pt-5">
      {/* Compact section header on the page gradient, as on Integrations. */}
      <div>
        <h1 className="text-foreground text-xl leading-tight font-semibold">Content</h1>
        <p className="text-muted-foreground mt-0.5 text-sm leading-5">
          Write, score and review product content for your catalog.
        </p>
      </div>

      {/* One panel: the tab bar is its header strip and the page scrolls below it. */}
      <div className="bg-card flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border shadow-sm">
        <nav
          aria-label="Content sections"
          className="border-border/70 scrollbar-thin flex shrink-0 items-center gap-6 overflow-x-auto border-b px-6 pt-3.5"
        >
          {workTabs.map((item) => (
            <ContentTab key={item.to} item={item} isActive={isUnder(pathname, item.to)} />
          ))}
          {/* Setup pages as tabs too — one click each, after a divider. */}
          <span aria-hidden="true" className="bg-border mb-2.5 h-4 w-px shrink-0" />
          {settingsItems.map((item) => (
            <ContentTab key={item.to} item={item} isActive={isUnder(pathname, item.to)} />
          ))}
        </nav>

        <div className="min-h-0 min-w-0 flex-1 overflow-auto p-6">
          {/* Above every Content page: an outage explains the empty tables below it. */}
          <ContentHealthBanner />
          <Outlet />
        </div>
      </div>
    </div>
  );
}

/** One work tab: icon + label, underlined when its section is open. */
function ContentTab({ item, isActive }: { item: ContentNavItem; isActive: boolean }) {
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      // Preserve search params (e.g. the subsidiary) across tabs; unknown keys are
      // stripped by the destination route's validateSearch. A tab always opens its main list,
      // so a sub-view (Runs › Archived) is left behind.
      search={(prev: Record<string, unknown>) => ({ ...prev, view: undefined })}
      aria-current={isActive ? 'page' : undefined}
      className={tabClass(isActive)}
      onMouseEnter={item.preload}
      onFocus={item.preload}
    >
      <Icon aria-hidden="true" className="size-4 shrink-0" />
      {item.label}
    </Link>
  );
}
